// UTM tracking — captures campaign params on first visit and forwards them with conversions.
// Usage: import { initUTMTracking, getStoredUTM } from '../utils/utm';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
const STORAGE_KEY = 'hoibuur_utm';
const TIMESTAMP_KEY = 'hoibuur_utm_ts';
const TTL_MS = 1000 * 60 * 60 * 24 * 30; // 30 days

/**
 * Reads UTM parameters from the current URL. If any are present, stores them in
 * localStorage so they survive across page navigations and can be attached to
 * conversions later (registrations, joins, likes).
 */
export function initUTMTracking() {
  try {
    const params = new URLSearchParams(window.location.search);
    const captured = {};
    for (const key of UTM_KEYS) {
      const value = params.get(key);
      if (value) captured[key] = value;
    }
    // Add referrer for first-touch attribution
    if (Object.keys(captured).length > 0) {
      captured.referrer = document.referrer || null;
      captured.landing_page = window.location.pathname;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(captured));
      localStorage.setItem(TIMESTAMP_KEY, Date.now().toString());
    }
  } catch {
    // localStorage disabled — silent fail
  }
}

/**
 * Returns the stored UTM parameters, or null if expired/missing.
 */
export function getStoredUTM() {
  try {
    const ts = parseInt(localStorage.getItem(TIMESTAMP_KEY) || '0', 10);
    if (!ts || Date.now() - ts > TTL_MS) return null;
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Clears stored UTM data. Call after a conversion is registered server-side.
 */
export function clearUTM() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(TIMESTAMP_KEY);
  } catch {
    // ignore
  }
}

/**
 * Wraps a payload with UTM data. Use when posting conversions to backend.
 * Example: api.post('/auth/register', withUTM({ name, email }))
 */
export function withUTM(payload = {}) {
  const utm = getStoredUTM();
  return utm ? { ...payload, _utm: utm } : payload;
}
